import { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { useStores } from "store/useStore";
import {
  SystemBanner,
  SystemBannerCta,
} from "components/SystemBanner/SystemBanner";
import { TopBannerPriority } from "components/SystemBanner/topBannerSlot";
import {
  isAppTakenOver,
  useAppTakenOver,
} from "components/Billing/appTakeover";
import {
  DIALOG_SCOPE,
  getActiveAnnouncement,
  isAnnouncementDismissed,
  isForAudience,
  rememberAnnouncementDismissed,
} from "./announcements";
import { trackAnnouncement } from "./trackAnnouncement";
import FeatureAnnouncementDialog from "./FeatureAnnouncementDialog";

/**
 * Tells members about something we have just shipped: a line in the strip
 * above the navbar, and - when the announcement carries one - a centred dialog
 * with the feature's own control in it.
 *
 * The two close independently. Waving the dialog away should not take the
 * banner with it, because the banner is how you get the dialog back.
 */
const FeatureAnnouncementBanner = observer(() => {
  const { accountStore, uiStore } = useStores();
  const teamMemberId = accountStore.account?.teamMemberId;
  const announcement = getActiveAnnouncement();
  const knownMember = teamMemberId !== undefined && teamMemberId !== null;
  // The trial-expired, account-blocked and 2FA-locked screens replace the app
  // entirely. They sit at z-index 100 and this dialog at 1400, so without this
  // it would open straight over the top of them - and news about a feature is
  // not the thing to say to someone who has just been told they have lost
  // access. The banner already yields the strip by priority; the dialog has to
  // yield too, or the announcement walks around the very system this ships.
  const takenOver = useAppTakenOver();
  // Trials are left out of this one - see `audience` in announcementConfig.
  // The status is the derived one the account carries, so an account with no
  // payment row still reads as "trial" rather than as nothing.
  const forThisAccount = announcement
    ? isForAudience(announcement, accountStore.account?.status)
    : false;

  // Assume dismissed until proven otherwise. The account arrives a moment
  // after the first render, and dismissals are stored per member, so starting
  // at `false` would flash the banner at someone who closed it last week.
  const [dismissed, setDismissed] = useState(true);
  // Open/closed lives in uiStore, not here, because the profile menu opens it
  // too - closing the banner must not take the announcement, and the question
  // it asks, away for good. Everything else about it still belongs to this
  // component: it is the only thing that knows who the announcement is for.
  const dialog = uiStore.featureAnnouncementDialog;
  const dialogOpen = dialog.isOpen;
  const [seenTracked, setSeenTracked] = useState(false);

  useEffect(() => {
    if (!announcement || !knownMember) {
      return;
    }
    setDismissed(isAnnouncementDismissed(announcement.id, teamMemberId!));
    // The dialog opens by itself the first time, if the announcement asks for
    // it - otherwise most people would never see it, and the point of the
    // question is to reach the members who would not go looking. Not while the
    // app is taken over, though; `takenOver` is a dependency so it opens once
    // that clears rather than being skipped for good.
    if (
      // asked of the register rather than of `takenOver`, which is the value
      // from the render that scheduled this effect. The takeovers sit above
      // this component in DefaultLayout so they have already registered by
      // now, but the closure still holds what was true a moment earlier - and
      // acting on it opens the dialog, and records having opened it, for
      // somebody who never sees it.
      !isAppTakenOver() &&
      forThisAccount &&
      announcement.dialog?.autoOpen &&
      !isAnnouncementDismissed(announcement.id, teamMemberId!, DIALOG_SCOPE)
    ) {
      dialog.open();
      trackAnnouncement("dialog opened", announcement.id, "auto");
    }
  }, [
    announcement,
    knownMember,
    teamMemberId,
    takenOver,
    forThisAccount,
    dialog,
  ]);

  // Nothing optional on screen while the app is replaced - including a dialog
  // already open when the takeover arrived. It is not marked dismissed, so it
  // comes back when they are let back in.
  if (!announcement || takenOver || !forThisAccount) {
    return null;
  }

  const dismissBanner = () => {
    setDismissed(true);
    if (knownMember) {
      rememberAnnouncementDismissed(announcement.id, teamMemberId!);
    }
    trackAnnouncement("dismissed", announcement.id);
  };

  const closeDialog = () => {
    dialog.close();
    if (knownMember) {
      rememberAnnouncementDismissed(
        announcement.id,
        teamMemberId!,
        DIALOG_SCOPE
      );
    }
    trackAnnouncement("dialog closed", announcement.id);
  };

  const openDialog = () => {
    dialog.open();
    // Two events, deliberately: "clicked" is the banner CTA doing whatever it
    // does, "dialog opened" is a dialog reaching the screen however it got
    // there. They were the same thing only while the dialog had one way in.
    trackAnnouncement("clicked", announcement.id, "dialog");
    trackAnnouncement("dialog opened", announcement.id, "banner");
  };

  return (
    <>
      <SystemBanner
        id="feature-announcement"
        priority={TopBannerPriority.featureAnnouncement}
        show={knownMember && !dismissed}
        tone="warning"
        onDismiss={dismissBanner}
        onShown={() => {
          // counted when it actually reaches the screen, not when the
          // announcement merely exists: someone behind the 2FA banner has not
          // seen this one
          if (!seenTracked) {
            setSeenTracked(true);
            trackAnnouncement("seen", announcement.id);
          }
        }}
        data-testid="feature-announcement-banner"
      >
        {announcement.message}{" "}
        <SystemBannerCta
          href={announcement.dialog ? undefined : announcement.ctaHref}
          onClick={
            announcement.dialog
              ? openDialog
              : () => trackAnnouncement("clicked", announcement.id, "link")
          }
          data-testid="feature-announcement-cta"
        >
          {announcement.ctaLabel}
        </SystemBannerCta>
      </SystemBanner>

      {announcement.dialog && (
        <FeatureAnnouncementDialog
          isOpen={dialogOpen}
          onClose={closeDialog}
          content={announcement.dialog}
        />
      )}
    </>
  );
});

export default FeatureAnnouncementBanner;
